// PRD.321 — Start From Existing Form.
//
// ONE clone algorithm, shared by the Forms list "Duplicate"/"Edit (Copy)"
// actions and by "New Form → Start from existing". Before this, the copy logic
// lived inline in `FormsListPage`; it is extracted here rather than reimplemented
// so the two entry points can never drift (PRD §16, §29).
//
// CLONE, DO NOT INHERIT. The result is a fully independent Draft definition:
// after the copy nothing reads its structure from the source, so editing,
// publishing or archiving either one leaves the other untouched.
//
// TRANSACTION BOUNDARY. This is a sequence of `db.execute(...)` calls, each its
// own CRUD transaction, so a failure part-way through is compensated instead:
// see `rollbackPartialClone` below.
//
// PRD.329 correction: this comment used to say a `db.writeTransaction(...)`
// would commit locally and never reach the server. The PRD.329 §9.3 spike
// disproved that — writes inside a write transaction are captured and upload as
// ONE batch (applied in one Postgres transaction). This path was not migrated
// (out of scope). If it ever is: inside the callback use only `tx.*`; calling
// `db.*` there deadlocks.

import type { FormDefinitionRow } from '@helios/data-access';

/** Minimal shape of the PowerSync handle this service needs. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
}

interface SourcePageRow {
  id: string;
  page_number: number;
  image_url: string;
  image_width: number;
  image_height: number;
  label: string | null;
}

interface SourceMappingRow {
  page_id: string;
  field_key: string;
  field_type: string;
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  config: string | null;
  sort_order: number;
}

export interface CloneFormDefinitionInput {
  orgId: string;
  /** Definition to copy. Must belong to `orgId`. */
  sourceFormId: string;
  /** Name for the new form. Blank falls back to "<source> (Copy)". */
  name?: string;
}

export interface CloneFormDefinitionResult {
  formDefinitionId: string;
  pagesCopied: number;
  mappingsCopied: number;
}

export type FormCloneErrorCode = 'source_not_found' | 'source_forbidden' | 'copy_failed';

export class FormCloneError extends Error {
  // Declared and assigned explicitly: parameter properties are not permitted
  // under this repo's `erasableSyntaxOnly` TypeScript setting.
  readonly code: FormCloneErrorCode;

  constructor(message: string, code: FormCloneErrorCode) {
    super(message);
    this.name = 'FormCloneError';
    this.code = code;
  }
}

/**
 * Definition columns that are deliberately NOT copied.
 *
 * `status`  — the clone always starts as a Draft, even from a Published source,
 *             so it can be reviewed before going live (PRD §7).
 * `version` — a new definition starts at 1; publication history is not carried.
 * `purpose` — an OPERATIONAL ASSIGNMENT, not builder configuration. At most one
 *             published form per org is active for a purpose, so copying
 *             `registration` onto a clone would queue up a second claimant to a
 *             live workflow the admin never asked for (PRD §14).
 * `updated_by`, timestamps — fresh metadata for a new record.
 */
export async function cloneFormDefinition(
  db: Db,
  input: CloneFormDefinitionInput,
): Promise<CloneFormDefinitionResult> {
  const { orgId, sourceFormId } = input;

  // ─── Authorize + snapshot the source ───
  //
  // Read once, up front, and build everything from that snapshot: a source
  // edited mid-clone cannot produce a half-old/half-new copy (PRD §23).
  const sourceRows = await db.getAll<FormDefinitionRow>(
    `SELECT * FROM form_definitions WHERE id = ?`,
    [sourceFormId],
  );
  const source = sourceRows[0];
  // Fail CLOSED: an unknown id and an id from another org are both refused,
  // and the messages stay generic so this cannot be used to probe for the
  // existence of another org's forms.
  if (!source) throw new FormCloneError('Source form not found', 'source_not_found');
  if (source.org_id !== orgId) {
    throw new FormCloneError('Source form is not accessible', 'source_forbidden');
  }

  const newFormId = crypto.randomUUID();
  const name = (input.name ?? '').trim() || `${source.name} (Copy)`;

  await db.execute(
    `INSERT INTO form_definitions
       (id, org_id, clinic_id, name, description, status, version, fields_schema,
        default_view, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, 'draft', 1, ?, ?, datetime('now'), datetime('now'))`,
    [
      newFormId,
      orgId,
      // Same clinic scope as the source, so a clinic-scoped form does not
      // silently widen to the whole org.
      source.clinic_id ?? null,
      name,
      source.description ?? null,
      // `fields_schema` is a jsonb blob owned solely by its definition, so
      // copying it verbatim already yields full independence — there is no
      // shared mutable row. Field KEYS are kept as-is on purpose: they are
      // namespaced per definition, and mapping/binding/save-orchestrator
      // lookups resolve by key. Regenerating them would break every mapping
      // and binding for no isolation gain.
      source.fields_schema ?? '[]',
      // Without this the clone of a Render-mode form opened in Form mode and
      // did not look like its source at all.
      source.default_view ?? 'form',
    ],
  );

  try {
    // ─── Template pages ───
    //
    // New page ROWS (never shared — pages are mutable and definition-owned),
    // pointing at the SAME `image_url`. Images are immutable Firebase Storage
    // objects and nothing in the app ever deletes them: `handleRemovePage`
    // drops the page row and its mappings only. So a shared URL is safe in
    // both directions — removing or replacing either form's template leaves
    // the other rendering (PRD §9.1 strategy A). Duplicating the binary would
    // buy nothing and cost storage.
    const pages = await db.getAll<SourcePageRow>(
      `SELECT * FROM form_template_pages WHERE form_definition_id = ? ORDER BY page_number ASC`,
      [sourceFormId],
    );

    const pageIdMap = new Map<string, string>();
    for (const page of pages) {
      const newPageId = crypto.randomUUID();
      pageIdMap.set(page.id, newPageId);
      await db.execute(
        `INSERT INTO form_template_pages
           (id, form_definition_id, page_number, image_url, image_width, image_height, label,
            created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [
          newPageId,
          newFormId,
          // Page order is part of the layout, so it is preserved exactly.
          page.page_number,
          page.image_url,
          page.image_width,
          page.image_height,
          page.label,
        ],
      );
    }

    // ─── Template mappings ───
    const mappings = await db.getAll<SourceMappingRow>(
      `SELECT * FROM form_template_mappings WHERE form_definition_id = ? ORDER BY sort_order ASC`,
      [sourceFormId],
    );

    for (const m of mappings) {
      const mappedPageId = pageIdMap.get(m.page_id);
      // A mapping whose page did not come across would otherwise be written
      // pointing at the SOURCE's page id — a cross-definition reference that
      // breaks the independence rule. Drop it instead.
      if (!mappedPageId) continue;
      await db.execute(
        `INSERT INTO form_template_mappings
           (id, form_definition_id, page_id, field_key, field_type,
            x_pct, y_pct, w_pct, h_pct, config, sort_order, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [
          crypto.randomUUID(),
          newFormId,
          mappedPageId,
          // `field_key` resolves within the copied `fields_schema`, which is
          // byte-identical, so the key still points at the clone's own field.
          m.field_key,
          m.field_type,
          m.x_pct,
          m.y_pct,
          m.w_pct,
          m.h_pct,
          m.config ?? '{}',
          m.sort_order,
        ],
      );
    }

    return {
      formDefinitionId: newFormId,
      pagesCopied: pages.length,
      mappingsCopied: mappings.filter((m) => pageIdMap.has(m.page_id)).length,
    };
  } catch (err) {
    // Compensating delete, because a real transaction is not available here.
    // Leaving a definition with half its pages would look like a working form
    // and print wrongly, which is worse than no form at all.
    await rollbackPartialClone(db, newFormId);
    throw new FormCloneError(
      `Failed to copy form contents: ${err instanceof Error ? err.message : String(err)}`,
      'copy_failed',
    );
  }
}

/**
 * Undo a clone that failed part-way.
 *
 * Children first, then the definition, so an interruption during cleanup never
 * strands rows pointing at a definition that no longer exists. Best-effort: if
 * cleanup itself fails the original error still surfaces, which is the more
 * useful one to report.
 */
async function rollbackPartialClone(db: Db, formDefinitionId: string): Promise<void> {
  for (const sql of [
    `DELETE FROM form_template_mappings WHERE form_definition_id = ?`,
    `DELETE FROM form_template_pages WHERE form_definition_id = ?`,
    `DELETE FROM form_definitions WHERE id = ?`,
  ]) {
    try {
      await db.execute(sql, [formDefinitionId]);
    } catch (cleanupErr) {
      console.warn('[form-clone] rollback step failed', cleanupErr);
    }
  }
}
